import { useEffect } from 'react';
import { bindActionCreators } from '@reduxjs/toolkit';
import { connect } from 'react-redux';
import { Redirect, Route, Switch, BrowserRouter, useLocation } from 'react-router-dom';

import Approvals from '../Approvals';
import DataSources from '../DataSources';
import ForgotPassword from '../ForgotPassword';
import Homepage from '../Homepage';
import IntacctAdmin from '../IntacctAdmin';
import Invite from '../Invite';
import Login from '../Login';
import NotFoundPage from '../NotFoundPage';
import Privacy from '../Privacy';
import Register from '../Register';
import ResetPassword from '../ResetPassword';
import Saml from '../Saml';
import Settings from '../Settings';
import Timesheet from '../Timesheet';
import Timesheets from '../Timesheets';
import Verify from '../Verify';

import PrivateRoute from '../../components/PrivateRoute';
import PublicRoute from '../../components/PublicRoute';
import SamlProtectedRoute from '../../components/SamlProtectedRoute';

import * as CompanyActions from '../../redux/Company/actions';
import * as DimensionsActions from '../../redux/Dimensions/actions';
import * as GeneralActions from '../../redux/General/actions';
import * as UserSettingsActions from '../../redux/UserSettings/actions';
import DeactivateCompanySuccessModal from '../../components/DeactivateCompanySuccessModal';

import useIdentify from '../../analytics/useIdentify';
import { selectShouldRefreshData } from '../../redux/selectors';

export const App = ({ getApprovalsCheck, getCompany, getDimensions, getUserSettings, shouldRefreshData, showDeactivateCompanyModal }) => {
  const location = useLocation();

  useIdentify();

  useEffect(() => {
    if (shouldRefreshData) {
      getCompany();
      getUserSettings();
      getApprovalsCheck();
      getDimensions();
    }
  }, [shouldRefreshData]);

  return (
    <>
      <BrowserRouter>
        <Switch>
          <PublicRoute exact path="/forgot-password" component={ForgotPassword} />
          <PublicRoute exact path="/invite/:inviteToken" component={Invite} />
          <PublicRoute exact path="/login" component={Login} />
          <PublicRoute exact path="/register" component={Register} />
          <PublicRoute exact path="/reset-password/:resetToken" component={ResetPassword} />

          <Route path="/home" component={Homepage} />
          <Route path="/privacy" component={Privacy} />
          <Route path="/saml" component={Saml} />
          <Route exact path="/verify" component={Verify} />

          <SamlProtectedRoute path="/intacct-admin" component={IntacctAdmin} />

          <PrivateRoute path="/approvals" component={Approvals} />
          <PrivateRoute path="/data-sources" component={DataSources} />
          <PrivateRoute path="/settings" component={Settings} />
          <PrivateRoute exact path="/timesheet/:id" component={Timesheet} paramsToValidate={{ id: 'uuid' }} />
          <PrivateRoute path="/timesheets" component={Timesheets} />

          <Route exact path="/">
            <Redirect
              to={{
                pathname: '/timesheets',
                from: location.pathname,
              }}
            />
          </Route>
          <Route>
            <NotFoundPage />
          </Route>
        </Switch>
      </BrowserRouter>
      {showDeactivateCompanyModal && <DeactivateCompanySuccessModal />}
    </>
  );
};

const mapStateToProps = (state) => ({
  shouldRefreshData: selectShouldRefreshData(state),
  showDeactivateCompanyModal: state.local.showDeactivateCompanyModal,
});

const mapDispatchToProps = (dispatch) => {
  const _company = bindActionCreators(CompanyActions, dispatch);
  const _dimensions = bindActionCreators(DimensionsActions, dispatch);
  const _general = bindActionCreators(GeneralActions, dispatch);
  const _userSettings = bindActionCreators(UserSettingsActions, dispatch);
  return {
    getApprovalsCheck: _general.getApprovalsCheckRequest,
    getCompany: _company.getCompanyRequest,
    getDimensions: _dimensions.getDimensionsRequest,
    getUserSettings: _userSettings.getUserSettingsRequest,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
